import { api, apiEndpoints } from '../../utils/apiService';

export interface PosSystemMeta {
  label: string;
  logo: string;
  color: string;
}

const POS_SYSTEMS: Record<string, PosSystemMeta> = {
  TOAST: {
    label: 'Toast',
    logo: '/images/logo/toast-logo.png',
    color: '#FF6F00',
  },
  FOODHUB: {
    label: 'FoodHub',
    logo: '/images/logo/foodhub-logo.svg',
    color: '#DC2626',
  },
  DELIVERECT: {
    label: 'Deliverect',
    logo: '/images/logo/deliverect-logo.svg',
    color: '#00D369',
  },
  CLOVER: {
    label: 'Clover',
    logo: '/images/logo/clover-logo.png',
    color: '#43842B',
  },
  SQUARE: {
    label: 'Square',
    logo: '/images/logo/square-logo.png',
    color: '#000000',
  },
  SHOPIFY: {
    label: 'Shopify',
    logo: '/images/logo/shopify-logo.png',
    color: '#96BF48',
  },
  LIGHTSPEED: {
    label: 'Lightspeed',
    logo: '/images/logo/lightspeed-logo.png',
    color: '#E42527',
  },
};

const POS_FALLBACK: PosSystemMeta = {
  label: 'POS System',
  logo: '',
  color: '#6B7280',
};

export function getPosInfo(posSystemKey: string): PosSystemMeta {
  return POS_SYSTEMS[posSystemKey] || POS_FALLBACK;
}

// ── GET /api/pi/integrations/orders/status/ ─────────────────────────────────────

export interface PosHubConnectionRow {
  connection_id: number;
  environment: string;
  status: string;
  is_active: boolean;
  is_primary_for_orders?: boolean;
  is_primary_for_menu_sync?: boolean;
  pos_system?: string;
  poshub_connection_status?: string;
  poshub_location_id?: string;
  poshub_account_id?: string;
  last_menu_sync_id?: string | null;
  provider?: string;
  provider_display_name?: string;
}

/** Legacy nested shape from GET .../poshub/status/ */
export interface PosHubStatusResponse {
  ok?: boolean;
  store_id?: number;
  current_pos_system?: string;
  poshub?: {
    has_connection?: boolean;
    has_active_connection?: boolean;
    connections?: PosHubConnectionRow[];
  };
  /** Flat shape from GET /api/pi/integrations/orders/status/ */
  has_active_connection?: boolean;
  has_connection?: boolean;
  connections?: PosHubConnectionRow[];
}

export function ordersStatusHasActiveConnection(
  data: PosHubStatusResponse | null,
): boolean {
  if (!data) return false;
  if (data.poshub?.has_active_connection === true) return true;
  if (data.has_active_connection === true) return true;
  return false;
}

export function currentPosSystemForOrdersCard(
  posStatus: PosHubStatusResponse | null,
  storeInfo: Record<string, unknown> | null,
): string | undefined {
  const fromApi = posStatus?.current_pos_system;
  if (typeof fromApi === 'string' && fromApi.trim() !== '') {
    return fromApi.trim();
  }
  const fromStore = storeInfo?.current_pos_system;
  if (typeof fromStore === 'string' && fromStore.trim() !== '') {
    return fromStore.trim();
  }
  return undefined;
}

/** Display name for the “Connected to …” label (OpenTable-first UX). */
export function reservationPartnerLabel(partner: string | undefined): string {
  if (!partner || !String(partner).trim()) return 'OpenTable';
  const u = String(partner).trim().toUpperCase().replace(/-/g, '_');
  if (u === 'OPENTABLE' || u === 'OPEN_TABLE') return 'OpenTable';
  return String(partner)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export async function fetchIntegrationsStoreInfo(
  storeId: string | null,
): Promise<Record<string, unknown> | null> {
  const [infoResp, storeResp] = await Promise.all([
    api.get(apiEndpoints.getStoreInfo()),
    storeId
      ? api.get(apiEndpoints.getStoreById(storeId))
      : Promise.resolve(null),
  ]);

  let merged: Record<string, unknown> | null = null;
  if (infoResp && infoResp.ok) {
    merged = await infoResp.json();
  }
  if (storeResp && storeResp.ok) {
    const detail = await storeResp.json();
    merged = { ...(merged || {}) };
    if (
      Object.prototype.hasOwnProperty.call(detail, 'current_pos_system') &&
      detail.current_pos_system != null
    ) {
      merged.current_pos_system = detail.current_pos_system;
    }
  }
  return merged;
}

export async function fetchOrdersIntegrationStatus(): Promise<PosHubStatusResponse | null> {
  const resp = await api.get(apiEndpoints.getOrdersIntegrationStatus());
  if (!resp?.ok) return null;
  return (await resp.json()) as PosHubStatusResponse | null;
}

export type PosConnectResult =
  | { redirectUrl: string }
  | { error: string }
  | null;

/**
 * Starts POS OAuth. Response problems resolve as `{ error }`; a network
 * failure rejects so the caller can show its own message.
 */
export async function startPosConnect(): Promise<PosConnectResult> {
  const resp = await api.post(apiEndpoints.startPosHubConnect());
  if (resp && resp.ok) {
    const data = await resp.json();
    const redirectUrl =
      data.authorize_url ?? data.authorization_url ?? data.redirect_url;
    if (redirectUrl) return { redirectUrl };
    return { error: 'No authorization URL returned. Please contact support.' };
  }
  if (resp) {
    const err = await resp.json().catch(() => null);
    return {
      error:
        err?.detail ||
        err?.error ||
        `Failed to start connection (${resp.status}).`,
    };
  }
  return null;
}
